import { PhoneHQButton, PhoneHQModal } from "ch3-ui-lib";
import { AlertCircle, FileText, Users } from "lucide-react";
import type {ContactImportFailure} from "./contactImportTypes";

interface ImportInfoDialogProps {
    /** Controlled open state. */
    open: boolean;
    /** Open state change handler. */
    onOpenChange: (open: boolean) => void;
    /** Fired when the user chooses to pick a file. */
    onChooseFile: () => void;
    /** Failed contacts returned by the bulk import endpoint. */
    failures?: ContactImportFailure[];
    /** Number of contacts accepted before validation failures were returned. */
    importedCount?: number;
}

const ImportInfoDialog = ({
    open,
    onOpenChange,
    onChooseFile,
    failures = [],
    importedCount = 0,
}: ImportInfoDialogProps) => {
    const hasFailures = failures.length > 0;

    const handleChooseFile = () => {
        onChooseFile();
        onOpenChange(false);
    };

    return (
        <PhoneHQModal
            open={open}
            onOpenChange={onOpenChange}
            title={hasFailures ? "Contacts not imported" : "Import contacts"}
            width={480}
            footer={
                <>
                    <PhoneHQButton variant="secondary" className="px-5" onClick={() => onOpenChange(false)}>
                        {hasFailures ? "Close" : "Cancel"}
                    </PhoneHQButton>
                    <PhoneHQButton className="px-5" onClick={handleChooseFile}>
                        {hasFailures ? "Choose another file" : "Choose file"}
                    </PhoneHQButton>
                </>
            }
        >
            <div className="flex flex-col">
                {hasFailures ? (
                    <div className="text-callout font-normal text-ink-secondary">
                        <div className="flex items-start gap-3 rounded-xl bg-amber-50 p-3 mb-4">
                            <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5"/>
                            <p className="leading-5">
                                {importedCount > 0
                                    ? `${importedCount} contact${importedCount === 1 ? "" : "s"} imported. `
                                    : ""}
                                {failures.length} contact{failures.length === 1 ? " was" : "s were"} not imported.
                            </p>
                        </div>
                        <div className="max-h-72 overflow-y-auto divide-y divide-zinc-200 border border-line rounded-xl">
                            {failures.map(failure => (
                                <div key={failure.id} className="p-3">
                                    <p className="font-semibold text-ink">{failure.name}</p>
                                    {failure.phoneNumbers.length > 0 && (
                                        <p className="text-ink-muted mt-0.5">
                                            {failure.phoneNumbers.join(", ")}
                                        </p>
                                    )}
                                    <ul className="mt-1 list-disc pl-4 text-red-600">
                                        {failure.messages.map((message, index) => (
                                            <li key={`${failure.id}-${index}`}>{message}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="text-callout font-normal text-ink-secondary">
                        <p className="leading-5 mb-4">
                            Choose a file to import your contacts. The following formats are supported:
                        </p>
                        <div className="flex items-start gap-3 mb-3">
                            <div className="flex items-center justify-center size-9 rounded-control bg-surface-muted shrink-0">
                                <FileText size={18} className="text-ink-muted"/>
                            </div>
                            <div>
                                <p className="leading-5 font-semibold text-ink">CSV file (.csv)</p>
                                <p className="leading-5 text-ink-muted">
                                    A Google Contacts export in CSV format.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="flex items-center justify-center size-9 rounded-control bg-surface-muted shrink-0">
                                <Users size={18} className="text-ink-muted"/>
                            </div>
                            <div>
                                <p className="leading-5 font-semibold text-ink">vCard file (.vcf)</p>
                                <p className="leading-5 text-ink-muted">
                                    A standard vCard contact file exported from most address books.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PhoneHQModal>
    );
};

export default ImportInfoDialog;
